import { useState } from 'react';
import { Mail, Download, ArrowLeft, Send, QrCode, Clock, Ticket } from 'lucide-react';

export default function OrderDetails() {
  const [note, setNote] = useState('');

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Order ORD-23401</h1>
          <p className="mt-0.5 text-xs text-slate-500">Home / Orders / ORD-23401</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </button>
          <button className="flex items-center px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600">
            Cancel Order
          </button>
        </div>
      </div>

      {/* Order and Customer Information */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Order Information */}
        <div className="p-5 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Order Information</h2>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Order ID</span>
              <span className="font-medium text-slate-800">ORD-23401</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Order Date</span>
              <span className="font-medium text-brand-blue">April 15, 2023, 14:30</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Order Status</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue">Confirmed</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Payment Status</span>
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700">Success</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Payment Method</span>
              <span className="text-slate-800">Credit Card (Visa)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Payment ID</span>
              <span className="text-slate-800">PAY-202304150123</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Currency</span>
              <span className="text-slate-800">LKR (Sri Lankan Rupee)</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-slate-100">
              <span className="text-slate-500">Total Amount</span>
              <span className="font-bold text-slate-900">LKR 12,500.00</span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="p-5 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Customer Information</h2>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Customer</span>
              <span className="font-medium text-slate-800">John Smith</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Email</span>
              <span className="font-medium text-brand-blue">john.smith@example.com</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Phone</span>
              <span className="text-slate-800">+94 77 123 4567</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Country</span>
              <span className="text-slate-800">Sri Lanka</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">NIC / Passport</span>
              <span className="text-slate-800">985432789V</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">User Since</span>
              <span className="text-slate-800">January 10, 2023</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Orders</span>
              <span className="text-slate-800">5</span>
            </div>
            <div className="flex justify-end pt-1">
              <a href="#" className="text-sm font-medium text-brand-blue hover:underline">View Full Profile &rarr;</a>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="bg-white border rounded-xl border-slate-200">
        <div className="p-5">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Order Items</h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs font-semibold text-left border-b text-slate-500 border-slate-200">
                  <th className="px-2 py-2">Item</th>
                  <th className="px-2 py-2">Merchant</th>
                  <th className="px-2 py-2">Quantity</th>
                  <th className="px-2 py-2">Unit Price</th>
                  <th className="px-2 py-2">Subtotal</th>
                  <th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="px-2 py-3">
                    <div className="font-medium text-slate-800">Sigiriya Fortress</div>
                    <div className="text-xs text-slate-500">Destination Ticket &middot; Apr 20, 2023</div>
                  </td>
                  <td className="px-2 py-3 font-medium text-brand-blue">Cultural Triangle Authority</td>
                  <td className="px-2 py-3 text-slate-700">3</td>
                  <td className="px-2 py-3 text-slate-700">LKR 3,000.00</td>
                  <td className="px-2 py-3 font-medium text-slate-900">LKR 9,000.00</td>
                  <td className="px-2 py-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue">Confirmed</span>
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <td colSpan={6} className="px-2 py-4">
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                      <div>
                        <div className="mb-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Ticket Type</div>
                        <span className="px-2 py-1 text-xs font-semibold rounded-md bg-brand-blue/10 text-brand-blue">Standard Entry</span>
                      </div>
                      <div>
                        <div className="mb-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Session</div>
                        <div className="flex items-center gap-1.5 text-sm text-slate-700">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          Morning &middot; 6:00 AM &ndash; 10:00 AM
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Ticket Origin</div>
                        <span className="px-2 py-1 text-xs font-semibold rounded-md bg-amber-50 text-amber-700">Foreign</span>
                      </div>
                      <div>
                        <div className="mb-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Age Group Breakdown</div>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="px-2 py-1 text-xs font-medium rounded-md bg-slate-200 text-slate-700">2 &times; Foreign Adult</span>
                          <span className="px-2 py-1 text-xs font-medium rounded-md bg-slate-200 text-slate-700">1 &times; Local Adult</span>
                        </div>
                      </div>
                      <div className="sm:text-right">
                        <div className="mb-1 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">E-Tickets</div>
                        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 sm:justify-end">
                          <Ticket className="w-3.5 h-3.5 text-slate-400" />
                          3 QR codes issued
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="px-2 py-3">
                    <div className="font-medium text-slate-800">Airport Transfer</div>
                    <div className="text-xs text-slate-500">Transportation &middot; Apr 19, 2023</div>
                  </td>
                  <td className="px-2 py-3 font-medium text-brand-blue">SL Travel Transport</td>
                  <td className="px-2 py-3 text-slate-700">1</td>
                  <td className="px-2 py-3 text-slate-700">LKR 3,500.00</td>
                  <td className="px-2 py-3 font-medium text-slate-900">LKR 3,500.00</td>
                  <td className="px-2 py-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue">Confirmed</span>
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} className="px-2 py-3 font-semibold text-right text-slate-700">Total</td>
                  <td colSpan={2} className="px-2 py-3 font-bold text-slate-900">LKR 12,500.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order Notes and Timeline */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Order Notes */}
        <div className="p-5 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Order Notes</h2>

          <div className="space-y-1 text-sm text-slate-600">
            <p>Customer requested early morning entry to Sigiriya.</p>
            <p>Airport pickup from Bandaranaike International Airport at 7:00 AM.</p>
          </div>

          <div className="mt-4">
            <input
              type="text"
              className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              placeholder="Add a note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* Order Timeline */}
        <div className="p-5 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Order Timeline</h2>

          <div className="space-y-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-brand-blue"></div>
              <div className="flex justify-between w-full ml-3 text-sm">
                <span className="font-medium text-slate-700">Order Created</span>
                <span className="text-slate-400">Apr 15, 14:30</span>
              </div>
            </div>

            <div className="flex items-center">
              <div className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-brand-blue"></div>
              <div className="flex justify-between w-full ml-3 text-sm">
                <span className="font-medium text-slate-700">Payment Successful</span>
                <span className="text-slate-400">Apr 15, 14:32</span>
              </div>
            </div>

            <div className="flex items-center">
              <div className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-brand-blue"></div>
              <div className="flex justify-between w-full ml-3 text-sm">
                <span className="font-medium text-slate-700">Order Confirmed</span>
                <span className="text-slate-400">Apr 15, 14:35</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-wrap justify-between gap-3">
        <button className="flex items-center px-4 py-2 text-sm font-semibold bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to List
        </button>

        <div className="flex gap-2">
          <button className="flex items-center px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark">
            <Send className="w-4 h-4 mr-2" />
            Send Receipt
          </button>
          <button className="flex items-center px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark">
            <QrCode className="w-4 h-4 mr-2" />
            Print QR Codes
          </button>
        </div>
      </div>
    </div>
  );
}
